/* oxlint-disable @typescript-eslint/no-explicit-any */
import type { ErrorReporter } from '@change-corgi/core/errorReporter/common';
import type { GqlClient } from '@change-corgi/core/gql';

import type { Session, SessionConfiguration } from '#src/shared/session';
import { getSession as _getSession, getSessionWithConfig } from '#src/shared/session';

import type { HydrationData } from '#src/app/types';

import { createFallbackSession, createFallbackSessionConfiguration } from './fallback';
import { createSessionGqlPath } from './gqlPath';

type Options = Readonly<{
	gql: GqlClient;
	errorReporter: ErrorReporter;
	hydrationData: HydrationData | undefined;
}>;

type Result = Readonly<{
	session: Session;
	sessionConfiguration: SessionConfiguration;
	sessionError?: true;
}>;

export async function getSession({ gql, errorReporter, hydrationData }: Options): Promise<Result> {
	if (hydrationData?.session && hydrationData.sessionConfiguration) {
		return { session: hydrationData.session, sessionConfiguration: hydrationData.sessionConfiguration };
	}

	const getSessionOptions: Parameters<typeof _getSession>[0] = {
		gqlFetch: async ({ rejectOnError, ...options }) =>
			// oxlint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion, @typescript-eslint/no-unsafe-return
			rejectOnError
				? (gql.fetch<Readonly<Record<string, unknown>>, Readonly<Record<string, unknown>>>({
						...options,
						path: createSessionGqlPath({ hydrationData }),
						rejectOnError,
					}) as Promise<any>)
				: gql.fetch<Readonly<Record<string, unknown>>, Readonly<Record<string, unknown>>>({
						...options,
						path: createSessionGqlPath({ hydrationData }),
						rejectOnError,
					}),

		reportError: errorReporter.report.bind(errorReporter),
	};

	if (!hydrationData?.session && hydrationData?.sessionConfiguration) {
		try {
			const session = await _getSession(getSessionOptions);

			return { session, sessionConfiguration: hydrationData.sessionConfiguration };
		} catch (e: any) {
			void errorReporter.report(new Error('Could not retrieve session', { cause: e }));
			return {
				session: createFallbackSession({ hydrationData }),
				sessionConfiguration: hydrationData.sessionConfiguration,
				sessionError: true,
			};
		}
	}

	try {
		const { session, sessionConfiguration } = await getSessionWithConfig(getSessionOptions);

		return { session, sessionConfiguration };
	} catch (e: any) {
		void errorReporter.report(new Error('Could not retrieve session and session configuration', { cause: e }));
		return {
			session: createFallbackSession({ hydrationData }),
			sessionConfiguration: createFallbackSessionConfiguration(),
			sessionError: true,
		};
	}
}
